import { useModel } from '@umijs/max';
import { authService } from '@/services/auth/authService';
import type { User } from '@/types/auth';

interface UsePermissionsReturn {
  hasPermission: (resource: string, action: string) => boolean;
  hasAnyPermission: (
    permissions: Array<{
      resource: string;
      action: 'read' | 'write' | 'delete' | 'admin';
    }>,
  ) => boolean;
  hasAllPermissions: (
    permissions: Array<{
      resource: string;
      action: 'read' | 'write' | 'delete' | 'admin';
    }>,
  ) => boolean;
  canAccessCountry: (country: string) => boolean;
  canAccessCulture: (locale: string) => boolean;
  canAccessReport: (reportId: string) => boolean;
  canAccessFeature: (feature: string) => boolean;
  canPerformAction: (reportId: string, actionIdentifier: string) => boolean;
  getReportPermissions: (reportId: string) => any;
  getAvailableActions: (reportId: string) => any[];
  getUserPermissions: (resource?: string) => string[];
  getUserCountries: () => string[];
  getAllowedCultures: () => string[];
  getUserReports: () => string[];
  getUserFeatures: () => string[];
  isAdmin: boolean;
  user: User | null;
  loading: boolean;
  refreshPermissions: () => Promise<void>;

  // New page action permission methods
  canPerformPageAction: (pageId: string, actionId: string) => boolean;
  getPageActions: (pageId: string) => any[];
  hasAnyPageAction: (pageId: string) => boolean;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { initialState, loading } = useModel('@@initialState');
  const user = initialState?.currentUser as User | null;

  const hasPermission = (resource: string, action: string): boolean => {
    // Use authService for page/action checking with raw backend pages
    return authService.hasPageAction(resource, `action:${action}`);
  };

  const hasAnyPermission = (
    permissions: Array<{
      resource: string;
      action: 'read' | 'write' | 'delete' | 'admin';
    }>,
  ): boolean => {
    return permissions.some(({ resource, action }) =>
      hasPermission(resource, action as 'read' | 'write' | 'delete' | 'admin'),
    );
  };

  const hasAllPermissions = (
    permissions: Array<{
      resource: string;
      action: 'read' | 'write' | 'delete' | 'admin';
    }>,
  ): boolean => {
    return permissions.every(({ resource, action }) =>
      hasPermission(resource, action as 'read' | 'write' | 'delete' | 'admin'),
    );
  };

  const canAccessCountry = (country: string): boolean => {
    // Use authService for country access checking (includes backend permissions)
    return authService.canAccessCountry(country);
  };

  const canAccessCulture = (locale: string): boolean => {
    return authService.canAccessCulture(locale);
  };

  const canAccessReport = (pageId: string): boolean => {
    // Use authService with raw backend pages
    return authService.hasPageAccess(pageId);
  };

  const canAccessFeature = (feature: string): boolean => {
    if (!user) return false;

    // Check if user has access to all features
    if (user.features?.includes('*')) return true;

    // Check specific feature access
    return user.features?.includes(feature) || false;
  };

  const canPerformAction = (
    pageId: string,
    actionIdentifier: string,
  ): boolean => {
    // Use authService with raw backend pages
    return authService.hasPageAction(pageId, actionIdentifier);
  };

  const getReportPermissions = (pageId: string): any => {
    const cachedUser = authService.getCachedUser();
    if (!cachedUser || !cachedUser.pages) return null;

    return cachedUser.pages.find((p: any) => p.identifier === pageId) || null;
  };

  const getAvailableActions = (pageId: string): any[] => {
    const cachedUser = authService.getCachedUser();
    if (!cachedUser || !cachedUser.pages) return [];

    const page = cachedUser.pages.find((p: any) => p.identifier === pageId);
    return page?.actions || [];
  };

  const getUserPermissions = (pageId?: string): string[] => {
    const cachedUser = authService.getCachedUser();
    if (!cachedUser || !cachedUser.pages) return [];

    if (pageId) {
      const page = cachedUser.pages.find((p: any) => p.identifier === pageId);
      return page?.actions?.map((a: any) => a.identifier) || [];
    }

    // Return all action identifiers across all pages
    return cachedUser.pages.flatMap(
      (p: any) => p.actions?.map((a: any) => a.identifier) || [],
    );
  };

  const getUserCountries = (): string[] => {
    return user?.countries || [];
  };

  const getAllowedCultures = (): string[] => {
    return authService.getAllowedCultures();
  };

  const getUserReports = (): string[] => {
    // Return all page identifiers
    const cachedUser = authService.getCachedUser();
    return cachedUser?.pages?.map((p: any) => p.identifier) || [];
  };

  const getUserFeatures = (): string[] => {
    return user?.features || [];
  };

  const refreshPermissions = async (): Promise<void> => {
    try {
      // Re-fetch current user data which includes permissions
      await authService.getCurrentUser();
      // The initial state will be updated automatically through the authService
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    }
  };

  // Page action permission methods using raw backend pages
  const canPerformPageAction = (pageId: string, actionId: string): boolean => {
    return authService.hasPageAction(pageId, actionId);
  };

  const getPageActions = (pageId: string): any[] => {
    return getAvailableActions(pageId);
  };

  const hasAnyPageAction = (pageId: string): boolean => {
    return authService.hasPageAccess(pageId);
  };

  const isAdmin = user?.roles?.some((role) => role.name === 'admin') || false;

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessCountry,
    canAccessCulture,
    canAccessReport,
    canAccessFeature,
    canPerformAction,
    getReportPermissions,
    getAvailableActions,
    getUserPermissions,
    getUserCountries,
    getAllowedCultures,
    getUserReports,
    getUserFeatures,
    refreshPermissions,
    isAdmin,
    user,
    loading: loading || false,

    // New page action permission methods
    canPerformPageAction,
    getPageActions,
    hasAnyPageAction,
  };
};

export default usePermissions;
